// import logo from './logo.svg';
// import './App.css';

// function App() {
//   return (
//     <div className="App">
//       <header className="App-header">
//         <img src={logo} className="App-logo" alt="logo" />
//         <p>
//           Edit <code>src/App.js</code> and save to reload.
//         </p>
//         <a
//           className="App-link"
//           href="https://reactjs.org"
//           target="_blank"
//           rel="noopener noreferrer"
//         >
//           Learn React
//         </a>
//       </header>
//     </div>
//   );
// }

// export default App;

// src/App.js
import 'bootstrap/dist/css/bootstrap.min.css';
import './mobile-styles.css';  // Import the mobile-specific styles
import React, { Suspense, useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import ProductDetailsPage from './pages/ProductDetailsPage';
import { CartProvider } from './contexts/CartContext';
import { LanguageProvider } from './contexts/LanguageContext';
import ErrorBoundary from './components/ErrorBoundary';
import FirebaseErrorBoundary from './components/FirebaseErrorBoundary';
import OfflineIndicator from './components/OfflineIndicator';
import { useAuth } from './Auth/AuthContext';
import AddressSetupPage from './pages/AddressSetupPage';
import { hasValidDeliveryAddress, getUserHomeRoute, debugUserProfile, needsProfileCompletion } from './utils/userUtils';
import UserStateDebug from './components/UserStateDebug';
import MobileBottomNav from './components/MobileBottomNav';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase/firebaseConfig';

const Home = React.lazy(() => import("./pages/Home"));
const About = React.lazy(() => import("./pages/About"));
const Cart = React.lazy(() => import("./pages/CartPage"));
const Checkout = React.lazy(() => import("./pages/Checkout"));
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const Location = React.lazy(() => import("./pages/Location"));
const Admin = React.lazy(() => import("./pages/Admin"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const CategoryPage = React.lazy(() => import('./pages/CategoryPage'));
const CompleteProfilePage = React.lazy(() => import('./pages/CompleteProfilePage'));
const OrdersPage = React.lazy(() => import('./pages/OrdersPage'));
const OrderHistoryPage = React.lazy(() => import('./pages/OrderHistoryPage'));
const AdminOrdersPage = React.lazy(() => import('./pages/adminOrdersPage'));
const AdminUploadPage = React.lazy(() => import('./pages/adminUploadPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));

// Delivery system components
const DeliveryDashboard = React.lazy(() => import('./components/DeliveryDashboard'));
const UserManagement = React.lazy(() => import('./components/UserManagement'));
const GoogleMapsTest = React.lazy(() => import('./components/GoogleMapsTest'));

// Admin Dashboard
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));


// Component to protect routes based on user type
const ProtectedRoute = ({ children, requiredUserType, redirectTo = "/login" }) => {
  const { user, userProfile, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{height: '100vh'}}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" style={{width: '3rem', height: '3rem'}}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="text-primary">Loading...</h5>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }
  
  if (requiredUserType && userProfile?.userType !== requiredUserType) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Component to redirect if address is already set up
const AddressSetupRedirect = ({ children }) => {
  const { userProfile, userType, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{height: '100vh'}}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" style={{width: '3rem', height: '3rem'}}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="text-primary">Loading...</h5>
        </div>
      </div>
    );
  }
  
  // Check if user has a valid delivery address in their profile
  const hasValidAddress = hasValidDeliveryAddress(userProfile);
  console.log(`🔍 AddressSetupRedirect - checking address validity: ${hasValidAddress}`);
  console.log('🔍 Address check details:', {
    hasDeliveryAddressProperty: !!userProfile?.deliveryAddress,
    coordinatesPresent: userProfile?.deliveryAddress?.coordinates?.latitude && userProfile?.deliveryAddress?.coordinates?.longitude,
    legacyCoordinatesPresent: userProfile?.deliveryAddress?.lat && userProfile?.deliveryAddress?.lng
  });
  
  // If user already has address, redirect to appropriate page based on userType
  if (hasValidAddress) {
    console.log('✅ User already has address, redirecting based on userType:', userType);
    debugUserProfile(userProfile, 'AddressSetupRedirect - User with Address');
    
    const homeRoute = getUserHomeRoute(userType);
    console.log(`🏠 Redirecting to home route: ${homeRoute}`);
    return <Navigate to={homeRoute} replace />;
  }
  
  console.log('🗺️ User needs address setup');
  debugUserProfile(userProfile, 'AddressSetupRedirect - User without Address');
  return children;
};

// Component to handle address setup requirement
const AddressRequiredRoute = ({ children }) => {
  const { userProfile, loading, refreshUserProfile } = useAuth();
  const [errorState, setErrorState] = useState(false);
  const [validationAttempts, setValidationAttempts] = useState(0);
  
  // Add effect to handle address validation errors
  useEffect(() => {
    if (!loading && userProfile && errorState && validationAttempts < 3) {
      const validateAddress = async () => {
        try {
          console.log(`🔄 Attempting to refresh user profile after address error (attempt ${validationAttempts + 1}/3)...`);
          await refreshUserProfile();
          
          // Check if the address is now valid
          const refreshedProfile = await refreshUserProfile();
          const isValid = hasValidDeliveryAddress(refreshedProfile);
          
          if (isValid) {
            console.log('✅ Address validation successful after refresh');
            setErrorState(false);
          } else {
            console.error('❌ Address still invalid after refresh');
            setValidationAttempts(prev => prev + 1);
          }
        } catch (refreshError) {
          console.error('❌ Failed to refresh user profile:', refreshError);
          setValidationAttempts(prev => prev + 1);
        }
      };
      
      validateAddress().catch(error => {
        console.error('🚨 Unhandled error in address validation:', error);
        setValidationAttempts(prev => prev + 1);
      });
    }
  }, [errorState, loading, userProfile, refreshUserProfile, validationAttempts]);
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{height: '100vh'}}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" style={{width: '3rem', height: '3rem'}}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="text-primary">Loading...</h5>
        </div>
      </div>
    );
  }
  
  // If there was an error and we're still recovering, show a message
  if (errorState && validationAttempts < 3) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{height: '100vh'}}>
        <div className="text-center">
          <div className="spinner-border text-warning mb-3" role="status" style={{width: '3rem', height: '3rem'}}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="text-warning">Verifying delivery address...</h5>
          <p className="text-muted">Attempt {validationAttempts + 1}/3</p>
        </div>
      </div>
    );
  }
  
  try {
    // First check if we have a userProfile
    if (!userProfile) {
      console.error('❌ No user profile available');
      return <Navigate to="/login" replace />;
    }

    // Safely check address validity
    let addressIsValid = false;
    try {
      addressIsValid = hasValidDeliveryAddress(userProfile);
    } catch (addressError) {
      console.error('❌ Error checking address validity:', addressError);
      setErrorState(true);
    }

    if (!addressIsValid) {
      console.log('❌ User needs address setup, redirecting to address-setup');
      debugUserProfile(userProfile, 'AddressRequiredRoute - User without Address');
      return <Navigate to="/address-setup" replace />;
    }
    
    console.log('✅ User has valid address coordinates, allowing access');
    debugUserProfile(userProfile, 'AddressRequiredRoute - User with Address');
    return children;
  } catch (error) {
    console.error('❌ Error in AddressRequiredRoute:', error);
    
    if (validationAttempts >= 3) {
      // After 3 failed attempts, redirect to address setup
      console.log('❌ Multiple address validation failures, redirecting to address-setup');
      return <Navigate to="/address-setup" replace />;
    }
    
    setErrorState(true);
    return (
      <div className="d-flex justify-content-center align-items-center" style={{height: '100vh'}}>
        <div className="text-center">
          <div className="spinner-border text-danger mb-3" role="status" style={{width: '3rem', height: '3rem'}}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="text-danger">Address validation error</h5>
          <p className="text-muted">Attempting to recover...</p>
          <div className="mt-3">
            <pre style={{textAlign: 'left', fontSize: '12px', backgroundColor: 'var(--background-color, #F8F9FA)', padding: '10px', borderRadius: '5px'}}>
              {JSON.stringify({error: error.message, location: 'AddressRequiredRoute'}, null, 2)}
            </pre>
            <button className="btn btn-sm btn-outline-primary mt-2" onClick={() => window.location.href = '/address-setup'}>
              Go to Address Setup
            </button>
          </div>
        </div>
      </div>
    );
  }
};

// Component to handle profile completion requirement
// DEPRECATED: Profile completion is no longer required
// Users will provide details during checkout
const ProfileRequiredRoute = ({ children }) => {
  const { userProfile, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{height: '100vh'}}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" style={{width: '3rem', height: '3rem'}}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="text-primary">Loading...</h5>
        </div>
      </div>
    );
  }
  
  // Simply pass through - profile completion no longer required
  console.log('✅ ProfileRequiredRoute: Bypassing profile check, allowing access');
  return children;
};

function App() {
  const [cartCount, setCartCount] = useState(0);
  const { user } = useAuth();
  
  // Effect to add mobile navigation body class
  useEffect(() => {
    document.body.classList.add('has-mobile-nav');
    
    return () => {
      document.body.classList.remove('has-mobile-nav');
    };
  }, []);
  
  // Effect to track cart count for the mobile nav
  useEffect(() => {
    if (!user) {
      setCartCount(0);
      return;
    }

    const unsub = onSnapshot(doc(db, 'incart', user.uid), (docSnap) => {
      const itemsObj = docSnap.exists() ? docSnap.data().items || {} : {};
      const total = Object.values(itemsObj).reduce((acc, item) => acc + (item.qty || 1), 0);
      setCartCount(total);
    });

    return () => unsub();
  }, [user]);
  
  return (
    <LanguageProvider>
      <ErrorBoundary>
        <FirebaseErrorBoundary>
          {/* <UserStateDebug /> */}
          <CartProvider>
            <Suspense fallback={
              <div className="d-flex justify-content-center align-items-center" style={{height: '100vh'}}>
                <div className="text-center">
                  <div className="spinner-border text-primary mb-3" role="status" style={{width: '3rem', height: '3rem'}}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <h5 className="text-primary">Loading ForFeeder...</h5>
                </div>
              </div>
            }>
          <Routes>
            {/* Public routes - accessible without login */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/about" element={<About />} />
            <Route path="/" element={<Home />} />
            <Route path="/category/:category" element={<CategoryPage />} />
            <Route path="/cart" element={<Cart />} />
            <Route 
              path="/product/:group/:subgroup/:productId" 
              element={<ProductDetailsPage />} 
            />
            
            {/* Address setup route - requires login but with simplified protection */}
            <Route 
              path="/address-setup" 
              element={
                <ProtectedRoute>
                  {/* Direct access to AddressSetupPage without additional redirects */}
                  <AddressSetupPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Checkout requires login only, address can be added during checkout */}
            <Route 
              path="/checkout" 
              element={
                <ProtectedRoute>
                  <Checkout />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/orders" 
              element={
                <ProtectedRoute>
                  <OrdersPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/orderhistory" 
              element={
                <ProtectedRoute>
                  <OrderHistoryPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Profile completion */}
            <Route 
              path="/complete-profile" 
              element={
                <ProtectedRoute>
                  <CompleteProfilePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/location" 
              element={
                <ProtectedRoute>
                  <Location />
                </ProtectedRoute>
              } 
            />
            
            {/* Admin routes */}
            <Route 
              path="/admin-dashboard" 
              element={
                <ProtectedRoute requiredUserType="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/adminorders" 
              element={
                <ProtectedRoute requiredUserType="admin">
                  <AdminOrdersPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/adminupload" 
              element={
                <ProtectedRoute requiredUserType="admin">
                  <AdminUploadPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/users" 
              element={
                <ProtectedRoute requiredUserType="admin">
                  <UserManagement />
                </ProtectedRoute>
              } 
            />
            
            {/* Delivery routes */}
            <Route 
              path="/delivery-dashboard" 
              element={
                <ProtectedRoute requiredUserType="delivery">
                  <DeliveryDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Test route for Google Maps AdvancedMarkerElement */}
            <Route 
              path="/maps-test" 
              element={<GoogleMapsTest />} 
            />
            
            {/* 404 route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        
        {/* Mobile Bottom Navigation */}
        <MobileBottomNav cartCount={cartCount} />
          </CartProvider>
        </FirebaseErrorBoundary>
      </ErrorBoundary>
    </LanguageProvider>
  );
}

export default App;